/**
 * Users Service
 * 
 * Handles user-related API operations.
 * Used for fetching team members for assignment, etc.
 * 
 * API endpoints:
 * - GET /api/users - Get all users
 * - GET /api/users?active=true - Get only active users
 */

import type { User } from '../data/types';
import { apiFetch, type ApiResponse } from './api';

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

interface UsersResponse {
  users: User[];
}

// ============================================================================
// GET USERS
// ============================================================================

/**
 * Fetches all active team members from the API
 */
export async function getUsers(): Promise<ApiResponse<User[]>> {
  const result = await apiFetch<UsersResponse>('/users?active=true');
  
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }
  
  return { data: result.data.users, success: true };
}

/**
 * Fetches all users (including inactive) from the API
 */
export async function getAllUsers(): Promise<ApiResponse<User[]>> {
  const result = await apiFetch<UsersResponse>('/users');
  
  if (!result.success) {
    return { data: [], success: false, error: result.error };
  }
  
  return { data: result.data.users, success: true };
}

/**
 * Fetches a single user by ID
 * Note: Currently fetches all users and filters - could be optimized with /users/:id endpoint
 */
export async function getUserById(userId: string): Promise<ApiResponse<User | null>> {
  const result = await getAllUsers();
  
  if (!result.success) {
    return { data: null, success: false, error: result.error };
  }
  
  const user = result.data.find(u => u.userId === userId);
  return { data: user || null, success: true };
}

/**
 * Gets analysts (non-admin users) for assignment dropdowns
 */
export async function getAnalysts(): Promise<ApiResponse<User[]>> {
  const result = await getUsers();
  
  if (!result.success) {
    return result;
  }
  
  const analysts = result.data.filter(u => 
    u.role === 'analyst' || u.role === 'senior_analyst'
  );
  
  return {
    data: analysts,
    success: true,
  };
}

/**
 * Find a user by email address
 */
export async function getUserByEmail(email: string): Promise<ApiResponse<User | null>> {
  const result = await getAllUsers();
  
  if (!result.success) {
    return { data: null, success: false, error: result.error };
  }
  
  const user = result.data.find(u => u.email.toLowerCase() === email.toLowerCase());
  return { data: user || null, success: true };
}
